import { FC } from 'react'

import {
  Flex,
  GreyGrey60,
  Primary12SemiBold,
  Primary13Regular,
  Text
} from '@sequencehq/core-components'

import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { usePublishedQuote } from 'Cube/view/layouts/quote/hooks'
import { useQuoteRecipientRows } from 'Cube/view/layouts/quote/components/QuoteRecipientsNotifyList'
import { QUOTE_RECIPIENTS_COPY } from './quoteRecipients.constants'
import { QuoteRecipientList } from './QuoteRecipientList'
import { QuoteRecipient, toRecipientGroups } from './quoteRecipients.utils'

/**
 * Who this quote is for, and how far each of them has got with it: the people
 * being asked to sign, then the people signing it back on our side, then the
 * people only copied in.
 */
export const QuoteRecipients: FC = () => {
  const ctx = useCubeContext()
  const publishedQuote = usePublishedQuote().data

  /**
   * The published variant is the one recipients were emailed a link to, so it -
   * not the draft being edited - knows who has signed and when each of them was
   * last written to. A quote that has never been published has no such variant,
   * and the draft in front of us is then the only list of who it will go to.
   */
  const { rows } = useQuoteRecipientRows(
    publishedQuote ?? ctx.queries.rawData.data.quote
  )

  const { signers, countersigners, copied } = toRecipientGroups(rows)
  const showEmptyState =
    signers.length === 0 && countersigners.length === 0 && copied.length === 0

  return (
    <Flex gap="16px" flexDir="column">
      {showEmptyState && (
        <Text color={GreyGrey60} {...Primary13Regular}>
          {QUOTE_RECIPIENTS_COPY.emptyState}
        </Text>
      )}

      <RecipientGroup
        title={QUOTE_RECIPIENTS_COPY.signers}
        recipients={signers}
        data-testid="quote.recipients.signers"
      />

      <RecipientGroup
        title={QUOTE_RECIPIENTS_COPY.countersigners}
        recipients={countersigners}
        data-testid="quote.recipients.countersigners"
      />

      <RecipientGroup
        title={QUOTE_RECIPIENTS_COPY.copied}
        recipients={copied}
        data-testid="quote.recipients.copied"
      />
    </Flex>
  )
}

const RecipientGroup: FC<{
  title: string
  recipients: QuoteRecipient[]
  'data-testid': string
}> = ({ title, recipients, 'data-testid': dataTestId }) => {
  if (recipients.length === 0) return null

  return (
    <Flex gap="12px" flexDir="column" data-testid={dataTestId}>
      <Text {...Primary12SemiBold} color={GreyGrey60}>
        {title}
      </Text>

      <QuoteRecipientList>
        {recipients.map(recipient => (
          <QuoteRecipientList.Item
            key={recipient.email}
            name={recipient.name}
            email={recipient.email}
            notificationStatus={recipient.notificationStatus}
            signedAt={recipient.signedAt}
            lastNotifiedAt={recipient.lastNotifiedAt}
          />
        ))}
      </QuoteRecipientList>
    </Flex>
  )
}
